import { useEffect, useRef } from 'react';

const BASE_FAVICON = '/favicon.svg';
const BADGE_SIZE = 12;
const CANVAS_SIZE = 32;

/**
 * Draws a notification-count badge on the favicon.
 * Restores the original favicon when count is 0.
 */
export function useFaviconBadge(count: number) {
  const linkRef = useRef<HTMLLinkElement | null>(null);

  useEffect(() => {
    // Find or create the favicon link element
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    linkRef.current = link;

    if (count <= 0) {
      link.href = BASE_FAVICON;
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      link.href = BASE_FAVICON;
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Draw original favicon
      ctx.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE);

      // Draw badge circle
      const badgeX = CANVAS_SIZE - BADGE_SIZE / 2 - 1;
      const badgeY = BADGE_SIZE / 2 + 1;
      const radius = BADGE_SIZE / 2;

      // White outline for contrast
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, radius + 1.5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      // Red badge
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, radius, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();

      // Badge text
      const label = count > 9 ? '9+' : String(count);
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${count > 9 ? 7 : 8}px -apple-system, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, badgeX, badgeY + 0.5);

      // Apply to favicon
      if (linkRef.current) {
        linkRef.current.href = canvas.toDataURL('image/png');
      }
    };

    img.onerror = () => {
      // If SVG fails to load as image, draw badge on blank canvas
      ctx.fillStyle = '#2563eb';
      ctx.beginPath();
      ctx.roundRect(0, 0, CANVAS_SIZE, CANVAS_SIZE, 8);
      ctx.fill();

      const badgeX = CANVAS_SIZE - BADGE_SIZE / 2 - 1;
      const badgeY = BADGE_SIZE / 2 + 1;
      const radius = BADGE_SIZE / 2;

      ctx.beginPath();
      ctx.arc(badgeX, badgeY, radius + 1.5, 0, Math.PI * 2);
      ctx.fillStyle = '#ffffff';
      ctx.fill();

      ctx.beginPath();
      ctx.arc(badgeX, badgeY, radius, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();

      const label = count > 9 ? '9+' : String(count);
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${count > 9 ? 7 : 8}px -apple-system, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, badgeX, badgeY + 0.5);

      if (linkRef.current) {
        linkRef.current.href = canvas.toDataURL('image/png');
      }
    };

    img.src = BASE_FAVICON;

    return () => {
      // Restore on unmount
      if (linkRef.current) {
        linkRef.current.href = BASE_FAVICON;
      }
    };
  }, [count]);
}
